/**
 * Per-event "extras" — the payload fields each Stripe event declares
 * ON TOP OF the 14 common fields. Lives in its own file so
 * `supported-events.ts` stays under the 1000-line cap.
 *
 * Each `*_EXTRAS` const is typed `as const` so TypeScript preserves
 * the literal `required: true | false` and `type: 'string' | 'int' | 'boolean'`
 * markers. The corresponding `StripeEventPayload<EventType>` mapped
 * type in `supported-events.ts` depends on those literals to derive
 * the correct required/optional field set.
 *
 * Adding / changing a field here is the single source of truth. The
 * runtime `payloadSchema.fields` array AND the compile-time
 * `StripeEventPayload<EventType>` type both flow from these consts.
 *
 * `internal: true` is used to mark a field that the extractor populates
 * on `triggerInstances.payload` (so the contact-sync mapper can read it
 * and the typed `StripeEventPayload<E>` shape stays accurate) but that
 * is FILTERED OUT of the user-facing `payloadSchema` written to Convex
 * by `toCanonicalPayloadSchema`. Use for opaque IDs (`subscriptionId`,
 * `invoiceId`, `checkoutSessionId`, etc.) that have no marketing-copy
 * value — the Test dialog form, the merge-tag picker, and
 * `validateAndResolvePayload` never see them.
 */

export const NO_EXTRAS = {} as const

export const CHECKOUT_EXTRAS = {
  checkoutSessionId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'Checkout Session ID.',
    exampleValue: 'cs_test_123',
  },
  paymentStatus: {
    type: 'string',
    required: false,
    description: 'Stripe Checkout payment status.',
    exampleValue: 'paid',
  },
  sessionMode: {
    type: 'string',
    required: false,
    description: 'Stripe Checkout mode.',
    exampleValue: 'payment',
  },
} as const

export const CUSTOMER_DELETED_EXTRAS = {
  deleted: {
    type: 'boolean',
    required: true,
    internal: true,
    description: 'Whether the Stripe object is deleted.',
    exampleValue: true,
  },
} as const

export const SUBSCRIPTION_BASIC_EXTRAS = {
  subscriptionId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'Subscription ID.',
    exampleValue: 'sub_test_123',
  },
} as const

export const SUBSCRIPTION_UPDATED_EXTRAS = {
  subscriptionId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'Subscription ID.',
    exampleValue: 'sub_test_123',
  },
  previousPlan: {
    type: 'string',
    required: false,
    description:
      'Price / plan id BEFORE the update, derived from `event.data.previous_attributes`. Empty when the update did not touch the plan.',
    exampleValue: 'price_starter_monthly',
  },
  newPlan: {
    type: 'string',
    required: false,
    description:
      'Price / plan id AFTER the update. Always set when the plan changed; empty otherwise.',
    exampleValue: 'price_pro_monthly',
  },
  previousQuantity: {
    type: 'int',
    required: false,
    description:
      'Seat / item quantity BEFORE the update from `previous_attributes`. Empty when quantity did not change.',
    exampleValue: 1,
  },
  newQuantity: {
    type: 'int',
    required: false,
    description: 'Seat / item quantity AFTER the update.',
    exampleValue: 5,
  },
  previousStatus: {
    type: 'string',
    required: false,
    description:
      'Subscription status BEFORE the update (e.g. trialing). Empty when status did not change.',
    exampleValue: 'trialing',
  },
} as const

export const INVOICE_BASIC_EXTRAS = {
  invoiceId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'Invoice ID.',
    exampleValue: 'in_test_123',
  },
  invoiceNumber: {
    type: 'string',
    required: false,
    description: 'Invoice number when available.',
    exampleValue: '0001',
  },
} as const

export const INVOICE_CREATED_EXTRAS = {
  invoiceId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'Invoice ID.',
    exampleValue: 'in_test_123',
  },
  invoiceNumber: {
    type: 'string',
    required: false,
    description: 'Invoice number when available.',
    exampleValue: '0001',
  },
  billingReason: {
    type: 'string',
    required: false,
    description:
      'Why the invoice was created (subscription_create, manual, upcoming, etc.).',
    exampleValue: 'subscription_create',
  },
} as const

export const INVOICE_UPCOMING_EXTRAS = {
  invoiceId: {
    type: 'string',
    required: false,
    internal: true,
    description: 'Upcoming invoice ID when available.',
    exampleValue: 'in_test_123',
  },
} as const

export const QUOTE_EXTRAS = {
  quoteId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'Quote ID.',
    exampleValue: 'qt_test_123',
  },
} as const

/**
 * `charge.refunded` carries the charge-level refund totals only.
 *
 * Stripe's own docs (https://docs.stripe.com/refunds#refund-events)
 * say for `charge.refunded`:
 *
 *   > "Listen to `refund.created` for information about the refund."
 *
 * The Charge object's `refunds` field is an expandable list that is
 * NOT auto-expanded in webhook payloads on API ≥ `2022-11-15`, so
 * per-refund details (id, reason, status, payment intent) are
 * unreliable when read from a `charge.refunded` event. Use
 * `REFUND_CREATED_EXTRAS` below for refund-specific automations.
 */
export const CHARGE_REFUNDED_EXTRAS = {
  chargeId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'The original charge ID.',
    exampleValue: 'ch_test_123',
  },
  amountRefunded: {
    type: 'int',
    required: false,
    description:
      'Total amount refunded on this charge so far, in the smallest currency unit.',
    exampleValue: 4900,
  },
} as const

/**
 * `refund.created` is the canonical event for refund-specific
 * automations (refund acknowledgment emails, dispute prevention
 * flows, etc.). `event.data.object` is a Refund object — see
 * https://docs.stripe.com/api/refunds/object — so all refund fields
 * are guaranteed to be inlined.
 */
export const REFUND_CREATED_EXTRAS = {
  refundId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'Refund ID (the Refund object id).',
    exampleValue: 're_test_123',
  },
  chargeId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'The charge the refund applies to (`refund.charge`).',
    exampleValue: 'ch_test_123',
  },
  paymentIntentId: {
    type: 'string',
    required: false,
    internal: true,
    description:
      'The payment intent the refund applies to (`refund.payment_intent`), when the charge was made via a PaymentIntent.',
    exampleValue: 'pi_test_123',
  },
  refundAmount: {
    type: 'int',
    required: false,
    description:
      'Amount of THIS refund, in the smallest currency unit (`refund.amount`). Distinct from the charge-level `amountRefunded` total.',
    exampleValue: 4900,
  },
  refundCurrency: {
    type: 'string',
    required: false,
    description:
      'Three-letter ISO currency code of the refund (`refund.currency`).',
    exampleValue: 'usd',
  },
  refundReason: {
    type: 'string',
    required: false,
    description:
      'Stripe-supplied refund reason (`refund.reason`): duplicate, fraudulent, requested_by_customer, or expired_uncaptured_charge.',
    exampleValue: 'requested_by_customer',
    fallbackValue: 'requested',
  },
  refundStatus: {
    type: 'string',
    required: false,
    description:
      'Refund status (`refund.status`): pending, succeeded, failed, canceled, or requires_action.',
    exampleValue: 'succeeded',
  },
} as const

export const PAYMENT_INTENT_FAILED_EXTRAS = {
  paymentIntentId: {
    type: 'string',
    required: true,
    internal: true,
    description: 'Payment intent ID.',
    exampleValue: 'pi_test_123',
  },
  failureCode: {
    type: 'string',
    required: false,
    description:
      'Stripe `last_payment_error.code` — high-level error category (e.g. `card_declined`, `expired_card`, `incorrect_cvc`).',
    exampleValue: 'card_declined',
  },
  failureMessage: {
    type: 'string',
    required: false,
    description:
      'Stripe-supplied human-readable failure message from `last_payment_error.message`.',
    exampleValue: 'Your card was declined.',
    fallbackValue: 'Your payment could not be processed.',
  },
  declineCode: {
    type: 'string',
    required: false,
    description:
      "Stripe `last_payment_error.decline_code` — the card issuer's specific decline reason (e.g. `insufficient_funds`, `expired_card`, `generic_decline`, `lost_card`, `stolen_card`). MORE ACTIONABLE than `failureCode` for dunning flows because it lets you tailor recovery copy per decline reason.",
    exampleValue: 'insufficient_funds',
  },
} as const
